import ExternalLink from "../../shared/assets/external-link.svg";
import { themeAtom } from "../../shared/atoms";
import { VoyageCargoM } from "../models/voyage.interfaces";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

type CargoInfoProps = {
  cargo: VoyageCargoM[];
  gotoLink: () => void;
};

function formatCargoDate(value?: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function buildOptionText(optionPercentage?: string | number, optionType?: string) {
  const percentage = optionPercentage ? `${optionPercentage}%` : "";
  const type = optionType?.trim() ?? "";

  return [percentage, type].filter(Boolean).join(" ") || "-";
}

export function VoyageCargo({ cargo, gotoLink }: Readonly<CargoInfoProps>) {
  const theme = useAtomValue(themeAtom);
  const cargoItem = cargo[0];

  const cargoId = cargoItem?.CARGO_ID || "-";
  const cargoName = cargoItem?.CARGO || "-";
  const cpQuantity = cargoItem?.CP_QUANTITY ? `${cargoItem.CP_QUANTITY}` : "-";
  const laycanFrom = formatCargoDate(cargoItem?.LAYCAN_FROM_LOCAL);
  const laycanTo = formatCargoDate(cargoItem?.LAYCAN_TO_LOCAL);
  const laycan = laycanFrom === "-" && laycanTo === "-" ? "-" : `${laycanFrom} - ${laycanTo}`;
  const charterer = cargoItem?.CHARTERER || "-";
  const department = cargoItem?.DEPARTMENT || "-";
  const option = buildOptionText(cargoItem?.OPTION_PERCENTAGE, cargoItem?.OPTION_TYPE);

  const labelClassName = "text-xs font-normal";
  const valueClassName = twMerge("mt-1 text-sm font-normal", theme.vesselDetails.info);

  return (
    <div
      className={twMerge(
        "w-full rounded-lg border",
        theme.home.tailwind.panelBackground,
        theme.home.tailwind.textDefault,
        theme.uwhcAssistant.chatUi.panelBorder,
      )}
    >
      <div
        className={twMerge(
          "rounded-t-lg border-b px-6 py-4",
          theme.uwhcAssistant.chatUi.panelBorder,
          theme.opsKnowledgeAssistant.chatUi.headerBg,
        )}
      >
        <div className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>
          Cargo <span className={twMerge("font-normal", theme.vesselDetails.info)}>#{cargoId}</span>
        </div>
      </div>

      <div className="space-y-4 p-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
              Cargo
            </div>
            <div className={valueClassName}>{cargoName}</div>
          </div>

          <div>
            <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
              CP Quantity
            </div>
            <div className={valueClassName}>{cpQuantity}</div>
          </div>
        </div>

        <div>
          <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
            Laycan
          </div>
          <div className={valueClassName}>{laycan}</div>
        </div>

        <div>
          <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
            Charterer
          </div>
          <div className={valueClassName}>{charterer}</div>
        </div>

        <div>
          <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
            Department
          </div>
          <div className={valueClassName}>{department}</div>
        </div>

        <div>
          <div className={labelClassName} style={{ color: "var(--greyscale-40, #899996)" }}>
            Option
          </div>
          <div className={valueClassName}>{option}</div>
        </div>

        <div className="pt-2">
          <button
            type="button"
            onClick={gotoLink}
            className="inline-flex items-center text-sm font-normal text-green hover:text-[#176A5E]"
          >
            Cargo&apos;s description & instructions
            <img src={ExternalLink} alt="" width="16" height="16" className="ml-1" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
